import { compose, withStateHandlers, getContext, withHandlers, withProps } from 'recompose';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import { withTranslation } from 'react-i18next';
import { memo } from 'react';
import { includes, prop, map } from 'ramda';

import { uiActions } from '../../../../state/ui';
import { newTaskActions, newTaskSelectors } from '../../../../state/newTask';
import { getProjectSprint } from '../../../../state/project/selectors';
import Sprint from './sprint';
import { getTimeStringBySeconds } from '../../../../utils/helpers/dateHelpers';
import { exportSprintTasksAsCSV } from '../../../../utils/helpers/exportHelpers';
import { withWindowWidth } from '../../../../utils/enchancers';
import { SPRINT_STATUS } from '../../../../constants/sprints';
import { projectSelectors } from '../../../../state/project';
import tasksApi from '@/api/tasks.service';
import { callNotification } from '@/utils/helpers/notifies';

const mapStateToProps = (state, props) => {
  const sprint = getProjectSprint(state)(props.id);
  const tasksEntities = projectSelectors.getProjectSprintTasks(state);
  const authorsEntities = projectSelectors.getProjectTasksAuthors(state);
  const assigneesEntities = projectSelectors.getProjectTasksAssignees(state);
  const modulesEntities = newTaskSelectors.getModulesEntities(state);

  return {
    item: sprint,
    tasksEntities,
    authorsEntities,
    assigneesEntities,
    modulesEntities,
  };
};

const mapDispatchToProps = {
  setOpenModal: uiActions.openModal,
  getModulesTaskCrud: newTaskActions.getModulesTaskCrudRequest,
};

const onOpenTabSprintStateHandler = ({ isOpenSprintTab }) => (val = null) => ({
  isOpenSprintTab: val !== null ? val : !isOpenSprintTab,
});

const onEditHandler = ({ id, setOpenModal, setSelectedSprintId }) => () => {
  setSelectedSprintId(id);
  setOpenModal('editSprintModal');
};

const onDeleteHandler = ({
  id, setOpenModal, setSelectedSprintId, onOpenTabSprint,
}) => () => {
  setSelectedSprintId(id);
  onOpenTabSprint(false);
  setOpenModal('deleteSprintModal');
};

const onCompleteHandler = ({
  id, setOpenModal, setSelectedSprintId, onOpenTabSprint,
}) => () => {
  setSelectedSprintId(id);
  onOpenTabSprint(false);
  setOpenModal('completeSprintModal');
};

const onStartHandler = ({
  id, setOpenModal, setSelectedSprintId, onOpenTabSprint,
}) => () => {
  setSelectedSprintId(id);
  onOpenTabSprint(true);
  setOpenModal('startSprintModal');
};

const onExportHandler =
  ({ item, tasksEntities, authorsEntities, assigneesEntities, modulesEntities }) =>
    () => {
      const expandedTasks =
        item && item.tasks
          ? map((taskId) => {
            const task = tasksEntities[taskId];
            if (!task) return null;

            const author =
              task.created_by && authorsEntities[task.created_by]
                ? authorsEntities[task.created_by]
                : null;

            const assigneeUser =
              task.assignee && assigneesEntities[task.assignee]
                ? assigneesEntities[task.assignee]
                : null;

            const module =
              task.project_module_id && modulesEntities[task.project_module_id]
                ? modulesEntities[task.project_module_id]
                : null;

            return {
              ...task,
              author,
              assigneeUser,
              module,
            };
          }, item.tasks).filter(Boolean)
          : [];

      const sprintWithExpandedTasks = {
        ...item,
        tasks: expandedTasks,
      };

      exportSprintTasksAsCSV(sprintWithExpandedTasks);
    };

const onImportHandler =
  ({ item, id, getTasksWithFilters, filters, getModulesTaskCrud }) =>
    () => {
      const input = document.createElement('input');
      input.type = 'file';
      input.accept = '.csv';

      input.onchange = async (e) => {
        const file = e.target.files[0];
        if (!file) return;

        await tasksApi.importTasks(item.project_id, id, file).then((res) => {
          callNotification.success(res.message)
          if (getTasksWithFilters) {
            getTasksWithFilters({ filters });
          }
          getModulesTaskCrud({ projectId: item.project_id });
        }).catch((err) => {
          callNotification.error(err.message);
        });
      };

      input.click();
    };

const enhance = compose(
  connect(mapStateToProps, mapDispatchToProps),
  withWindowWidth(),
  withTranslation(['common', 'tasks']),
  getContext({
    setSelectedSprintId: PropTypes.func.isRequired,
    getTasksWithFilters: PropTypes.func,
    filters: PropTypes.instanceOf(Object),
  }),
  withProps(({ item: { totalEstimated, totalSpend } }) => ({
    totalSpent: getTimeStringBySeconds(totalSpend),
    totalEstimated: getTimeStringBySeconds(totalEstimated),
  })),
  withStateHandlers(
    ({ item }) => ({
      isOpenSprintTab: includes(prop('status', item), [
        SPRINT_STATUS.ACTIVE,
        SPRINT_STATUS.NOT_ACTIVE,
      ]),
    }),
    {
      onOpenTabSprint: onOpenTabSprintStateHandler,
    },
  ),
  withHandlers({
    onEdit: onEditHandler,
    onDelete: onDeleteHandler,
    onComplete: onCompleteHandler,
    onStart: onStartHandler,
    onExport: onExportHandler,
    onImport: onImportHandler,
  }),
  memo,
);
export default enhance(Sprint);
